import { createContext, useState, useContext, useEffect } from "react";
import { getCurrentSession } from "../auth/cognito";
import { apiClient } from "../auth/apiClient";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userError, setUserError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const fetchCurrentUser = async (isLogin = false) => {
    try {
      setLoadingUser(true);
      const sessionData = await getCurrentSession();

      if (!sessionData) {
        setCurrentUser(null);
        setAccessToken(null);
        setLoadingUser(false);
        localStorage.removeItem("mg_session_hint");
        return;
      }

      const token = sessionData.session.getAccessToken().getJwtToken();
      setAccessToken(token);
      localStorage.setItem("mg_token", token);

      const res = await apiClient.get(
        `/users/me?login=${isLogin ? "true" : "false"}`
      );

      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("mg_session_hint");
          localStorage.removeItem("mg_token");
        }
        throw new Error(`HTTP error ${res.status}`);
      }

      const data = await res.json();
      setCurrentUser(data);
      setUserError(null);

      if (isLogin) localStorage.setItem("mg_session_hint", "1");
    } catch (err) {
      console.error("Failed to fetch user:", err);
      setUserError(err.message);
      setCurrentUser(null);
      localStorage.removeItem("mg_session_hint");
      localStorage.removeItem("mg_token");
    } finally {
      setLoadingUser(false);
    }
  };

  const refreshUser = (isLogin = false) => fetchCurrentUser(isLogin);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await fetchCurrentUser(false);
      } catch (error) {
        console.error("Auth initialization failed:", error);
      }
    };

    initializeAuth();
  }, []);

  // getCurrentSession() only refreshes the Cognito token when it's asked for,
  // and previously that only happened once at mount. A long-lived tab (e.g. an
  // admin dashboard left open for hours) would then silently run on a stale,
  // expired token: requests that tolerate missing auth (like the public
  // Discord list) quietly downgrade to anonymous instead of failing loudly,
  // which hid data an admin should have seen. Polling keeps the token current
  // well before Cognito's ~1 hour access-token expiry.
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(() => {
      getCurrentSession()
        .then((sessionData) => {
          if (!sessionData?.session) return;
          const token = sessionData.session.getAccessToken().getJwtToken();
          setAccessToken(token);
          localStorage.setItem("mg_token", token);
        })
        .catch((err) => console.warn("Background token refresh failed:", err));
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const value = {
    currentUser,
    loadingUser,
    userError,
    accessToken,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
